import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSortModule, Sort } from '@angular/material/sort';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { UtsavFormDialogComponent } from './utsav-form-dialog/utsav-form-dialog.component';
import { UtsavManageDialogComponent } from './utsav-manage-dialog/utsav-manage-dialog.component';
import { sortTableRows } from '../../common-files/table-sort';

@Component({
  selector: 'app-establishment-utsavs',
  standalone: true,
  imports: [CommonModule, MatSortModule, LoaderComponent],
  templateUrl: './establishment-utsavs.component.html',
  styleUrl: './establishment-utsavs.component.scss',
})
export class EstablishmentUtsavsComponent implements OnInit {
  utsavs: any[] = [];
  isLoading: boolean = false;
  // read-only for the Operator; add/edit/status for Admin+ (can_manage on 'utsav-setup').
  canManage: boolean = false;
  canActivate: boolean = false;
  private sortState: Sort = { active: '', direction: '' };

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage = this.permissionService.can('utsav-setup', 'can_manage');
    // activation is Central Establishment Admin only (an Estab Admin creates
    // utsavs as inactive and can only deactivate).
    this.canActivate = this.permissionService.can('utsav-setup', 'can_activate');
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.establishmentService.listUtsavs().subscribe({
      next: (res: any) => {
        this.utsavs = this.applySort(res?.data?.utsavs || []);
        this.isLoading = false;
      },
      error: (err) => {
        this.utsavs = [];
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load utsavs.');
      },
    });
  }

  sortUtsavs(sort: Sort): void {
    this.sortState = sort;
    this.utsavs = this.applySort(this.utsavs);
  }

  private applySort(utsavs: any[]): any[] {
    return sortTableRows(utsavs, this.sortState, {
      status: (utsav) => (Number(utsav.status) === 1 ? 'Active' : 'Inactive'),
    });
  }

  isActive(status: unknown): boolean {
    return Number(status) === 1;
  }

  openForm(utsav: any | null): void {
    const ref = this.dialog.open(UtsavFormDialogComponent, {
      width: '660px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { utsav },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  manage(utsav: any): void {
    const ref = this.dialog.open(UtsavManageDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { utsavId: Number(utsav.utsavId), name: utsav.name },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  deactivate(utsav: any): void {
    this.isLoading = true;
    this.establishmentService.setUtsavStatus(Number(utsav.utsavId), 0).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Utsav deactivated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to deactivate utsav.');
      },
    });
  }

  activate(utsav: any): void {
    if (!this.canActivate) return;
    this.isLoading = true;
    this.establishmentService.activateUtsav(Number(utsav.utsavId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Utsav activated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to activate utsav.');
      },
    });
  }
}
